import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { HttpInternalService } from 'src/app/data/service/http.service';

export interface InboxAutomatorMailbox {
  email: string;
  provider: 'Gmail' | 'Outlook' | string;
  emailAccountId: number;
  /** The choice as stored, including one kept through a downgrade. */
  isSwitchedOn: boolean;
  /** Switched on and the plan includes the feature. */
  isActive: boolean;
  activatedUtc: string | null;
}

/** A Gmail or Outlook mailbox connected to the organisation, for Settings > Mailboxes. */
export interface ConnectedEmailAccount {
  email: string;
  provider: 'Gmail' | 'Outlook';
  /** connected | reconnect (connected but not receiving mail) | disconnected */
  status: 'connected' | 'reconnect' | 'disconnected';
  problem: string | null;
  connectedBy: string | null;
}

export type InboxAutomatorMode = 'active' | 'activate' | 'available' | 'upgrade';

export interface InboxAutomatorState {
  /** The caller may use Inbox Automator: the plan includes it and their role allows it. */
  entitled: boolean;
  /** The organisation's plan includes it (for an admin) -- false means an upgrade is needed. */
  planIncludes: boolean;
  mailboxes: InboxAutomatorMailbox[];
}

/**
 * SD-1229: Inbox Automator per connected Gmail/Outlook mailbox (api/inbox-automator).
 * The state comes from the server, so it is correct whether or not the client resolves the
 * sidemenu from entitlements.
 */
@Injectable({ providedIn: 'root' })
export class InboxAutomatorService {
  /** Emits the organisation whose state changed, so every mailbox pill can reload. */
  readonly changed$ = new Subject<string>();

  /** One request per organisation for every pill on the page, briefly. */
  private cache = new Map<string, { at: number; state$: Observable<InboxAutomatorState> }>();
  private static readonly CacheMs = 30000;

  constructor(private http: HttpInternalService) {
    this.changed$.subscribe(org => this.cache.delete(org));
  }

  state(organizationId: string): Observable<InboxAutomatorState> {
    const hit = this.cache.get(organizationId);
    if (hit && Date.now() - hit.at < InboxAutomatorService.CacheMs) {
      return hit.state$;
    }

    const state$ = this.http.getRequest<InboxAutomatorState>('inbox-automator', { organizationId })
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.cache.set(organizationId, { at: Date.now(), state$ });
    return state$;
  }

  /** Drop the cached state, e.g. after a plan change or a mailbox connect. */
  invalidate(organizationId: string): void {
    this.cache.delete(organizationId);
  }

  activate(organizationId: string, email: string): Observable<any> {
    return this.http.postRequest<any>('inbox-automator/activate', { organizationId, email })
      .pipe(tap(() => this.changed$.next(organizationId)));
  }

  deactivate(organizationId: string, email: string): Observable<any> {
    return this.http.postRequest<any>('inbox-automator/deactivate', { organizationId, email })
      .pipe(tap(() => this.changed$.next(organizationId)));
  }

  /**
   * Disconnect a Gmail/Outlook mailbox: its token is revoked and cleared, mail stops arriving,
   * and its Inbox Automator switch goes off. Mail already received stays. Reconnecting is the
   * ordinary connect flow.
   */
  disconnect(organizationId: string, email: string): Observable<any> {
    return this.http.postRequest<any>('inbox-automator/disconnect', { organizationId, email })
      .pipe(tap(() => this.changed$.next(organizationId)));
  }

  /**
   * SD-1252: archive a disconnected mailbox, so the Emails rail lists it under Archived. Its mail
   * stays. A connected mailbox is refused (409); reconnecting an archived one brings it back.
   */
  archive(organizationId: string, email: string): Observable<any> {
    return this.http.postRequest<any>('inbox-automator/archive', { organizationId, email });
  }

  /** Every Gmail/Outlook mailbox of the organisation, connected or not; archived ones left out. */
  accounts(organizationId: string): Observable<{ accounts: ConnectedEmailAccount[] }> {
    return this.http.getRequest<{ accounts: ConnectedEmailAccount[] }>('inbox-automator/accounts', { organizationId });
  }

  unarchive(organizationId: string, email: string): Observable<any> {
    return this.http.postRequest<any>('inbox-automator/unarchive', { organizationId, email });
  }

  /**
   * What a mailbox's Inbox Automator control should offer, as the pill and the Emails rail
   * show it: on; off and switchable; on the plan but not for this user (ask an admin); or not
   * on the plan (upgrade). Null for a mailbox that is not connected.
   */
  static mode(state: InboxAutomatorState | null, email: string): InboxAutomatorMode | null {
    const mailbox = InboxAutomatorService.find(state, email);
    if (!state || !mailbox) { return null; }
    if (mailbox.isActive) { return 'active'; }
    if (state.entitled) { return 'activate'; }
    if (state.planIncludes) { return 'available'; }
    return 'upgrade';
  }

  static find(state: InboxAutomatorState | null, email: string): InboxAutomatorMailbox | null {
    const key = (email || '').trim().toLowerCase();
    return (state?.mailboxes || []).find(m => m.email === key) || null;
  }
}
